/** "Simulate": plays a simulated mission on this device through Sentio's real ingestion code, as
 *  `manage.py simulate_mission` does from a terminal (backend: missionagent/simulator). Development only:
 *  the button is not shown where the API says simulation is not enabled (PRODUCTION on, or a user who is
 *  neither Infiniwell staff nor an admin of the device's organization). The simulated device never presses End
 *  Mission, played to the end or stopped: the mission stays open until it is ended with the page's End mission button. */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useCookies } from "react-cookie";

import { errorMessage, fetchSimulation, startSimulation, stopSimulation } from "../api";
import type { Simulation, SimulationOptions, SimulationResponse, SimulationState } from "../types";
import { TOP_CONTROL_HEIGHT } from "./MissionPicker";

/** How often the status is read: while the dialog is open, and while a simulation is going with it closed (so
 *  the button shows when it ends). */
const POLL_MS = 2000;
const BACKGROUND_POLL_MS = 5000;

const ACTIVE: SimulationState[] = ["queued", "running", "stopping"];

const SPEEDS: { value: number; label: string; hint: string }[] = [
  { value: 1, label: "1×", hint: "real time, 30–60 min" },
  { value: 10, label: "10×", hint: "3–6 min" },
  { value: 40, label: "40×", hint: "about 1–2 min" },
  { value: 60, label: "60×", hint: "about 1 min" },
];

const STATE_LABEL: Record<SimulationState, string> = {
  queued: "Starting…",
  running: "Running",
  stopping: "Stopping…",
  finished: "Finished",
  stopped: "Stopped",
  failed: "Failed",
};

const STATE_COLOR: Record<SimulationState, "default" | "primary" | "success" | "warning" | "error"> = {
  queued: "default",
  running: "primary",
  stopping: "warning",
  finished: "success",
  stopped: "default",
  failed: "error",
};

const Steps = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "auto auto minmax(0, 1fr)",
  columnGap: theme.spacing(2),
  rowGap: theme.spacing(0.5),
  maxHeight: 320,
  overflowY: "auto",
  padding: theme.spacing(1, 1.5),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  fontSize: "0.8125rem",
  "& .time, & .kind": {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    whiteSpace: "nowrap",
  },
  "& .pending": { color: theme.palette.text.disabled },
  "& .unsent": { fontStyle: "italic" },
}));

function minutes(seconds: number | null | undefined): string {
  return seconds == null ? "—" : (seconds / 60).toFixed(1);
}

function isActive(simulation: Simulation | null | undefined): boolean {
  return !!simulation && ACTIVE.includes(simulation.state);
}

function SimulationProgress(props: { simulation: Simulation; scenarioLabel: (value: string | null) => string }) {
  const s = props.simulation;
  const total = s.total_seconds ?? 0;
  const value = total > 0 ? Math.min(100, (100 * s.simulated_seconds) / total) : 0;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Chip size="small" color={STATE_COLOR[s.state]} label={STATE_LABEL[s.state]} />
        <Typography variant="body2">
          {s.intervention_id ? (
            <>
              Mission <code>{s.intervention_id}</code> · {props.scenarioLabel(s.scenario)} · {s.options.speed}×
            </>
          ) : (
            "Starting the simulated device… (up to half a minute if the simulator has just restarted)"
          )}
        </Typography>
      </Box>
      {s.intervention_id ? (
        <Box>
          <LinearProgress variant="determinate" value={value} />
          <Typography variant="caption" color="text.secondary">
            {minutes(s.simulated_seconds)} / {minutes(s.total_seconds)} simulated min
          </Typography>
        </Box>
      ) : null}
      {s.error ? <Alert severity="error">{s.error}</Alert> : null}
      {s.steps.length > 0 ? (
        <Steps>
          {s.steps.map((step, index) => {
            const cls = `${step.done ? "" : "pending"} ${step.sent ? "" : "unsent"}`;
            return (
              <Box key={index} sx={{ display: "contents" }}>
                <span className={`time ${cls}`}>+{minutes(step.at)} min</span>
                <span className={`kind ${cls}`}>{step.kind}</span>
                <span className={cls}>
                  {step.text}
                  {step.sent ? "" : " (this device cannot send it)"}
                </span>
              </Box>
            );
          })}
        </Steps>
      ) : null}
    </Box>
  );
}

export function SimulateMissionButton(props: { deviceId: string; onNewMission?: (interventionId: string) => void }) {
  const { deviceId, onNewMission } = props;
  const [cookies] = useCookies(["access_token"]);
  const token: string | undefined = cookies.access_token;

  const [info, setInfo] = useState<SimulationResponse | null>(null);
  const [open, setOpen] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<SimulationOptions>({
    scenario: "random",
    speed: 40,
    facility: "random",
    seed: null,
  });

  const load = useCallback(async () => {
    if (!token) return;
    try {
      setInfo(await fetchSimulation(token, deviceId));
    } catch {
      setInfo(null); // not available here: no button
    }
  }, [token, deviceId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Polled while the dialog is open, and while a simulation is going (the button follows it to its end).
  const going = isActive(info?.simulation);
  useEffect(() => {
    if (!open && !going) return;
    const timer = setInterval(() => void load(), open ? POLL_MS : BACKGROUND_POLL_MS);
    return () => clearInterval(timer);
  }, [open, going, load]);

  // A simulation's mission, once it starts: have the page list (and follow) it straight away.
  const missionStarted = info?.simulation?.intervention_id ?? null;
  useEffect(() => {
    if (missionStarted) onNewMission?.(missionStarted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionStarted]);

  if (!info?.enabled) return null;

  const simulation = info.simulation;
  const running = isActive(simulation);
  const scenarioLabel = (value: string | null) => info.scenarios.find((s) => s.value === value)?.label ?? value ?? "—";
  const sends = info.simulated_as?.events ?? [];
  const facilities = info.facilities ?? [];
  // A device whose app has the mission buttons selects a receiving facility.
  const hasFacilityButtons = sends.includes("mission") && facilities.length > 0;

  const openDialog = () => {
    setError(null);
    setShowForm(!isActive(simulation));
    setOpen(true);
    void load();
  };

  const start = async () => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const answer = await startSimulation(token, deviceId, options);
      setInfo(answer);
      setShowForm(false);
      if (answer.detail) setError(answer.detail);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      setInfo(await stopSimulation(token, deviceId));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        variant={running ? "outlined" : "contained"}
        color="secondary"
        sx={{ height: TOP_CONTROL_HEIGHT }}
        onClick={openDialog}
      >
        {running ? "Simulating…" : "Simulate"}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Simulate a mission on device {deviceId}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {info.simulated_as
              ? `Simulated as a ${info.simulated_as.model} (${info.simulated_as.fidelity} packets); it sends ${
                  sends.join(", ") || "no events"
                }. `
              : "This device's model cannot be simulated yet. "}
            Its packets go through Sentio&apos;s real ingestion, as the device&apos;s own would: they show on the live
            screens and in the Mission Agent. Development only. The device never presses End Mission: when the
            simulation is over or stopped, the mission stays open until you end it with the End mission button.
          </Typography>
          {error ? <Alert severity="warning">{error}</Alert> : null}

          {showForm ? (
            <>
              <TextField
                select
                size="small"
                label="Scenario"
                value={options.scenario}
                onChange={(e) => setOptions({ ...options, scenario: e.target.value })}
                sx={{ maxWidth: 320 }}
              >
                {info.scenarios.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>

              <Box>
                <Typography variant="overline" component="div" color="text.secondary">
                  Speed
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={options.speed}
                  onChange={(_e, value) => value && setOptions({ ...options, speed: value })}
                >
                  {SPEEDS.map((s) => (
                    <ToggleButton key={s.value} value={s.value} sx={{ px: 1.5 }}>
                      {s.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary" component="div">
                  A mission takes {SPEEDS.find((s) => s.value === options.speed)?.hint}.
                </Typography>
              </Box>

              {hasFacilityButtons ? (
                <TextField
                  select
                  size="small"
                  label="Receiving facility"
                  value={options.facility}
                  onChange={(e) => setOptions({ ...options, facility: e.target.value })}
                  helperText="Random may select one, then change its mind."
                  sx={{ maxWidth: 320 }}
                >
                  <MenuItem value="random">Random</MenuItem>
                  <MenuItem value="none">None selected</MenuItem>
                  {facilities.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.name}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}

              <TextField
                size="small"
                type="number"
                label="Seed (optional)"
                helperText="The same seed replays the same mission."
                value={options.seed ?? ""}
                onChange={(e) =>
                  setOptions({ ...options, seed: e.target.value === "" ? null : Math.trunc(Number(e.target.value)) })
                }
                sx={{ maxWidth: 200 }}
              />

              {simulation && !running ? (
                <Typography variant="caption" color="text.secondary">
                  Last simulation: {STATE_LABEL[simulation.state].toLowerCase()}
                  {simulation.intervention_id ? ` (mission ${simulation.intervention_id})` : ""}
                  {simulation.error ? ` — ${simulation.error}` : ""}.{" "}
                  <Button size="small" onClick={() => setShowForm(false)}>
                    Show it
                  </Button>
                </Typography>
              ) : null}
            </>
          ) : simulation ? (
            <SimulationProgress simulation={simulation} scenarioLabel={scenarioLabel} />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          {showForm ? (
            <Button variant="contained" disabled={busy || running || !info.simulated_as} onClick={() => void start()}>
              Start
            </Button>
          ) : running ? (
            <Button
              variant="contained"
              color="error"
              disabled={busy || simulation?.state === "stopping"}
              onClick={() => void stop()}
            >
              Stop
            </Button>
          ) : (
            <Button variant="contained" onClick={() => setShowForm(true)}>
              New simulation
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
